import React, { useEffect, useState } from 'react';
import Select from 'react-select';
import { useForm, Controller } from 'react-hook-form';
import { Country } from 'country-state-city';
import { applicationStore } from 'src/store/ApplicationStore';
import { Button } from 'src/components/ui/Button';
import Preloader from 'src/components/ui/Preloader';
import { observer } from 'mobx-react';
import { NodeTypes } from 'src/enums/NodeTypes';
import { workflowStepIterator } from 'src/services/workflowStepIterator';
import { useNavigate } from 'react-router-dom';
import { LeftArrowWhite } from 'src/assets/images/svg/LeftArrow';
import LoadingComponent from 'src/components/ui/LoadingComponent';
import { parseServiceErrorMessage } from 'src/helpers/parseServiceError';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { getTranslatedCountryName } from 'src/constants/countryTranslations';
import { customStyles as flowSelectStyles } from 'src/statics/reactSelectCustomStyle';
import { FLOW_VAR } from 'src/theme/flowColorTokens';

const bankVerificationSelectStyles = (errors = false) => {
    const base = flowSelectStyles(errors);
    return {
        ...base,
        control: (provided, state) => ({
            ...base.control(provided, state),
            marginBottom: "1rem",
        }),
        valueContainer: (provided) => ({
            ...provided,
            display: "flex",
            alignItems: "center",
            padding: "2px 8px",
        }),
        singleValue: (provided) => ({
            ...base.singleValue(provided),
            display: "flex",
            alignItems: "center",
        }),
        placeholder: (provided) => ({
            ...base.placeholder(provided),
            opacity: 0.5,
        }),
        option: (provided, state) => ({
            ...base.option(provided, state),
            display: "flex",
            alignItems: "center",
        }),
    };
};

const BankVerification = observer(() => {
    const navigate = useNavigate();
    const { t, i18n } = useTranslation();
    const [countryData, setCountryData] = useState([]);
    const [bankOptions, setBankOptions] = useState([]);
    const [submitting, setSubmitting] = useState(false);
    const [loading, setLoading] = useState(false);
    const { control, handleSubmit, formState: { errors }, reset } = useForm();

    const countryOptions = countryData.map(country => ({
        ...country,
        label: getTranslatedCountryName(country.value, i18n.language)
    }));

    useEffect(() => {
        reset({ country: null, bank: null });
        setBankOptions([]);
    }, [i18n.language, reset]);

    useEffect(() => {
        const getCountries = async () => {
            setLoading(true);
            try {
                const response = await workflowStepIterator({
                    access_token: applicationStore.access_token,
                    node_id: applicationStore.workflowNextServiceResponse.node_id,
                    body: {
                        "plato": "plato"
                    },
                    action_index: applicationStore.workflowNextServiceResponse.index === 3 ? 3 : 0,
                    constextOfStep: { step: NodeTypes.VERIFICATION, "retry": applicationStore.workflowNextServiceResponse.retry }
                });
                if (response.data?.service_results[0].status !== 200) {
                    throw new Error("Error");
                }

                if (applicationStore.workflowNextServiceResponse.index === 3) {
                    applicationStore.setWorkflowNextServiceResponse(response.data);
                    if (response.data?.service_results[0].data.completed &&
                        response.data?.service_results[0].data.validated) {
                        navigate("/next", { replace: true });
                    } else {
                        applicationStore.setApplicationErrorObj({
                            header: t('bankVerification.stepFailedHeader'),
                            description: t('errors.loadingFlowDescription'),
                            buttonList: [
                                {
                                    label: t('actions.retry'),
                                    link: `/next`,
                                    variant: "primaryWithoutIcon"
                                }
                            ]
                        });
                        navigate("/error-page", { replace: true });
                    }
                }
                const countriesWithTwoCode = response.data?.service_results[0].data;

                const countries = countriesWithTwoCode
                    .map(country => Country.getCountryByCode(country))
                    .filter(countryObj => countryObj)
                    .map(countryObj => ({
                        value: countryObj.isoCode,
                        flag: countryObj.flag
                    }));

                setCountryData(countries);

            } catch (error) {
                applicationStore.setApplicationErrorObj({
                    header: t('bankVerification.stepFailedHeader'),
                    description: t('errors.loadingFlowDescription'),
                    buttonList: [
                        {
                            label: t('actions.retry'),
                            link: `/next`,
                            variant: "primaryWithoutIcon"
                        }
                    ]
                });
                navigate("/error-page", { replace: true });
            } finally {
                setLoading(false);
            }
        };

        if (applicationStore.workflowNextServiceResponse.step === NodeTypes.BANK_VERIFICATION) {
            getCountries();
        } else {
            navigate("/next", { replace: true });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchBankList = async (isoCode) => {
        try {
            const response = await workflowStepIterator({
                access_token: applicationStore.access_token,
                node_id: applicationStore.workflowNextServiceResponse.node_id,
                query: {
                    "country": isoCode
                },
                action_index: 1,
                constextOfStep: { step: NodeTypes.VERIFICATION, "retry": applicationStore.workflowNextServiceResponse.retry }
            })
            if (response.data?.service_results[0].status !== 200) {
                const rawMessage = response.data?.service_results[0]?.message;
                const parsedMessage = parseServiceErrorMessage(rawMessage);
                throw new Error(parsedMessage);
            }
            const banks = response.data?.service_results[0].data.map(bank => ({
                value: bank.id,
                label: bank.name,
                logo: bank.logo
            }));
            setBankOptions(banks);
        } catch (error) {
            toast.error(error?.message || t('bankVerification.notFound'));
        }
    };

    const customCountryOption = (props) => {
        const { data, innerRef, innerProps } = props;
        return (
            <div ref={innerRef} {...innerProps} style={{ display: 'flex', alignItems: 'center', padding: '10px' }}>
                <span style={{ marginRight: '10px' }}>{data.flag}</span>
                {data.label}
            </div>
        );
    };

    const customCountrySingleValue = ({ data }) => (
        <div style={{ display: 'flex', alignItems: 'center' }}>
            <span style={{ marginRight: '10px' }}>{data.flag}</span>
            {data.label}
        </div>
    );

    const customBankOption = (props) => {
        const { data, innerRef, innerProps } = props;
        return (
            <div ref={innerRef} {...innerProps} style={{ display: 'flex', alignItems: 'center', padding: '10px' }}>
                <img src={data.logo} alt={data.label} style={{ width: '20px', height: '20px', marginRight: '10px' }} />
                {data.label}
            </div>
        );
    };

    const customBankSingleValue = ({ data }) => (
        <div style={{ display: 'flex', alignItems: 'center' }}>
            <img src={data.logo} alt={data.label} style={{ width: '20px', height: '20px', marginRight: '10px' }} />
            {data.label}
        </div>
    );

    const onSubmit = async (data) => {
        const bankId = data.bank.value;
        setSubmitting(true);
        try {
            const response = await workflowStepIterator({
                access_token: applicationStore.access_token,
                node_id: applicationStore.workflowNextServiceResponse.node_id,
                query: {
                    "institution_id": bankId,
                    "aid": applicationStore.aidCode
                },
                action_index: 2,
                constextOfStep: { step: NodeTypes.VERIFICATION, "retry": applicationStore.workflowNextServiceResponse.retry }
            });
            if (response.data?.service_results[0].status !== 200) {
                throw new Error("Error");
            }
            if (response.data?.service_results[0].data.link) {
                applicationStore.setWorkflowNextServiceResponse({ ...response.data, index: 3 });
                localStorage.setItem("ApplicationStore", JSON.stringify(applicationStore));
                window.location.replace(response.data?.service_results[0].data.link);
            }
        } catch (error) {
            applicationStore.setApplicationErrorObj({
                header: t('bankVerification.stepFailedHeader'),
                description: t('errors.loadingFlowDescription'),
                buttonList: [
                    {
                        label: t('actions.retry'),
                        link: `/next`,
                        variant: "primaryWithoutIcon"
                    }
                ]
            });
            navigate("/error-page", { replace: true });
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <>
            {loading ? (
                <div className="custom-container">
                    <LoadingComponent />
                </div>
            ) : (
                <div className="custom-container">
                    <div className="flex justify-between flex-col items-center h-full">
                        <div className="">
                            <div className='text-left'>
                                <Button className="" variant="primaryBack" onClick={() => {
                                    navigate(`/?aid=${applicationStore.aidCode}`, { replace: true })
                                }}><LeftArrowWhite /></Button>
                            </div>
                            <div className='grid gap-[16px] mt-10'>
                                <div className="text-h5 font-bold text-flow-heading">{t('bankVerification.title')}</div>
                                <div className="text-body text-Gray900">{t('bankVerification.description')}</div>
                            </div>
                            <form className='mt-10' onSubmit={handleSubmit(onSubmit)}>
                                <Controller
                                    name="country"
                                    control={control}
                                    rules={{ required: t('bankVerification.countryRequired') }}
                                    render={({ field }) => (
                                        <Select
                                            {...field}
                                            options={countryOptions}
                                            onChange={(selectedOption) => {
                                                field.onChange(selectedOption);
                                                if (selectedOption) {
                                                    fetchBankList(selectedOption.value);
                                                    reset({ country: selectedOption, bank: null });
                                                }
                                            }}
                                            className='cursor-pointer notranslate'
                                            styles={bankVerificationSelectStyles(errors.country)}
                                            components={{ SingleValue: customCountrySingleValue, Option: customCountryOption }}
                                            placeholder={t('bankVerification.selectCountryPlaceholder')}
                                        />
                                    )}
                                />
                                {errors.country && <p style={{ color: FLOW_VAR.danger }}>{errors.country.message}</p>}


                                <Controller
                                    name="bank"
                                    control={control}
                                    rules={{ required: t('bankVerification.bankRequired') }}
                                    render={({ field }) => (
                                        <Select
                                            {...field}
                                            options={bankOptions}
                                            className='cursor-pointer notranslate'
                                            styles={bankVerificationSelectStyles(errors.bank)}
                                            components={{ SingleValue: customBankSingleValue, Option: customBankOption }}
                                            placeholder={t('bankVerification.selectBankPlaceholder')}
                                        />
                                    )}
                                />

                                {errors.bank && <p style={{ color: FLOW_VAR.danger }}>{errors.bank.message}</p>}

                                <div className="mt-6 w-full">
                                    <Button
                                        type="submit"
                                        className="mt-4 cursor-pointer"
                                        variant="primaryWithoutIcon"
                                        disabled={submitting}
                                    >
                                        <span className="me-1">{t('bankVerification.continue')}</span>
                                        {submitting && <Preloader />}
                                    </Button>
                                </div>

                            </form>
                        </div>
                        <div className="">

                        </div>
                    </div>
                </div>
            )}
        </>

    );
})

export default BankVerification;
